import {
  Button,
  Color,
  Icon,
  Flex,
  Link,
  SemanticColor,
} from '@bb/elements-web';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import MlsFilterDropdown from 'components/UniversalSearch/MlsFilterDropdown';
import { SearchBar } from 'components';
import type { FilteredMlsOptionsType } from 'components/UniversalSearch/types';

const QUEUE_MAX_SERVICE_APPTS = 6;
interface QueueToolbarProps {
  mlsOptions: { label: string; value: string }[];
  isMlsFilterVisible: boolean;
  setIsMlsFilterVisible: React.Dispatch<React.SetStateAction<boolean>>;
  filteredMlsOptions: FilteredMlsOptionsType;
  serviceMultipleCount?: number;
  searchText: string;
  isLoadingSearch?: boolean;
  isPreferencesModified: boolean;
  onSetFilteredMlsOptions: React.Dispatch<FilteredMlsOptionsType>;
  onOpenPrefsModal: () => void;
  onServiceMultipleClick: () => void;
  onSearch: (searchText: string) => void;
}

const QueueToolbar: React.FC<QueueToolbarProps> = ({
  mlsOptions,
  isMlsFilterVisible,
  setIsMlsFilterVisible,
  filteredMlsOptions,
  serviceMultipleCount,
  searchText,
  isLoadingSearch,
  isPreferencesModified,
  onOpenPrefsModal,
  onServiceMultipleClick,
  onSearch,
  onSetFilteredMlsOptions,
}) => {
  const isTooManyServiceMultiple = useMemo(
    () =>
      serviceMultipleCount && serviceMultipleCount > QUEUE_MAX_SERVICE_APPTS,
    [serviceMultipleCount],
  );

  return (
    <ToolbarContainer justify="flex-end" align="center" wrap="wrap">
      {serviceMultipleCount > 0 && (
        <StyledServiceButton
          size="small"
          dataCy="service-multiple-button"
          type="secondary"
          onClick={() => {
            if (!isTooManyServiceMultiple) onServiceMultipleClick();
          }}
          danger={isTooManyServiceMultiple}
        >
          {isTooManyServiceMultiple
            ? `Can only service ${QUEUE_MAX_SERVICE_APPTS} at once`
            : `Service Multiple: (${serviceMultipleCount})`}
        </StyledServiceButton>
      )}
      {isPreferencesModified && (
        <StyledLinkContainer>
          <Link
            color="danger"
            type="secondary"
            size="small"
            dataCy="open-prefs-modal-link"
            onClick={onOpenPrefsModal}
            underlineOnHover
          >
            Filtering queue based on preferences
          </Link>
        </StyledLinkContainer>
      )}
      <StyledPreferencesButton
        type="secondary"
        size="small"
        dataCy="open-prefs-modal-button"
        onClick={onOpenPrefsModal}
        minWidth={40}
      >
        <Icon icon="slidersOutlined" size={20} />
      </StyledPreferencesButton>
      <MlsFilterDropdown
        mlsOptions={mlsOptions}
        filteredMlsOptions={filteredMlsOptions}
        setFilteredMlsOptions={onSetFilteredMlsOptions}
        visible={isMlsFilterVisible}
        setVisible={setIsMlsFilterVisible}
        positionRelative
      />
      <InputSearchContainer>
        <SearchBar
          dataCy="address-agent-search-input"
          value={searchText}
          onChange={onSearch}
          loading={isLoadingSearch}
          allowClear
          placeholder="Address / Showing Agent"
        />
      </InputSearchContainer>
    </ToolbarContainer>
  );
};

export default React.memo(QueueToolbar);

const ToolbarContainer = styled(Flex)`
  gap: 3px;

  &&& {
    button[data-cy='button-mls-filter-dropdown'] {
      border-radius: 6px;
    }
  }
`;

const StyledServiceButton = styled(Button)`
  &&& {
    height: 32px;
    background-color: ${Color.WHITE};
  }
`;

const StyledPreferencesButton = styled(Button)`
  &&& {
    height: 32px;
    border-color: ${Color.BORDER_GREY};

    &:hover {
      border-color: ${Color.PRIMARY};
      & path {
        fill: ${Color.PRIMARY};
      }
    }
  }
`;

const InputSearchContainer = styled.div`
  flex: 1 1 250px;
  width: 250px;

  &&& {
    & .ant-input-affix-wrapper,
    & .ant5-input-affix-wrapper {
      height: 32px;
      border-radius: 6px;
    }

    & .ant-spin.ant-spin-spinning,
    & .ant5-spin.ant5-spin-spinning {
      margin-left: -1px;
    }
  }
`;

const StyledLinkContainer = styled.div`
  &&& {
    padding-left: 5px;
    padding-right: 5px;

    & a {
      font-size: 14px;
      font-weight: 500;
      color: ${SemanticColor.danger};
      text-decoration-color: ${SemanticColor.danger};
    }
  }
`;
